import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, errDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Wallet, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/auth/reset-password/${token}`);
        setEmail(r.data.email);
        setValid(true);
      } catch (err) {
        setError(errDetail(err.response?.data?.detail) || "Invalid or expired link");
      } finally {
        setChecking(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (pw !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated — signing you in…");
      setTimeout(() => nav("/login"), 1200);
    } catch (err) {
      setError(errDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FBFBF9] bg-grain flex items-center justify-center px-4"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-10 justify-center">
          <div className="w-10 h-10 rounded-md bg-[#1F4E3D] flex items-center justify-center">
            <Wallet className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-display text-2xl font-medium tracking-tight">Kosh</span>
        </div>

        <div className="bg-white border border-[#EAE8E3] rounded-lg p-8 sm:p-10">
          {checking ? (
            <div className="text-sm text-[#696E69]">Checking link…</div>
          ) : !valid ? (
            <>
              <div className="label-overline mb-2">Oops</div>
              <h1 className="font-display text-3xl tracking-tight font-light mb-2">
                Link invalid
              </h1>
              <p className="text-sm text-[#696E69] mb-6">
                {error || "This reset link is invalid, expired or already used."}
              </p>
              <Link
                to="/forgot-password"
                data-testid="request-new-link"
                className="text-sm text-[#1F4E3D] hover:underline"
              >
                Request a new link →
              </Link>
            </>
          ) : (
            <>
              <div className="label-overline mb-2">Choose a new password</div>
              <h1 className="font-display text-3xl tracking-tight font-light mb-1">
                Reset password
              </h1>
              <p className="text-sm text-[#696E69] mb-8">
                For <span className="font-mono-num text-[#212421]">{email}</span>
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">New password</Label>
                  <Input
                    data-testid="reset-new-password"
                    type="password"
                    required
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    className="mt-1.5"
                    autoFocus
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Confirm</Label>
                  <Input
                    data-testid="reset-confirm-password"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                {error && (
                  <div
                    data-testid="reset-error"
                    className="text-sm text-[#D26A59] bg-[#D26A59]/8 border border-[#D26A59]/20 rounded-md px-3 py-2"
                  >
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="reset-submit"
                  className="w-full bg-[#1F4E3D] hover:bg-[#163B2E] text-white rounded-lg py-2.5 h-auto mt-2"
                >
                  {busy ? "Updating…" : "Update password"}
                </Button>
              </form>
            </>
          )}
          <Link
            to="/login"
            className="mt-6 inline-flex items-center gap-1.5 text-xs text-[#696E69] hover:text-[#212421]"
          >
            <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
